import React from 'react';

import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import SettingsContext from 'src/features/administration/context/SettingsContext';
import { useGetPricingPlan } from 'src/features/pricingPlan/api/useGetPricingPlan';
import { CURRENCY_MAP, TICK_TYPE_MAP } from 'src/features/pricingPlan/utils/constants';
import { PricingPlanTierFormSchemaValues } from 'src/features/pricingPlan/utils/formSchema';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { TICK_TYPE } from 'src/features/pricingPlan/utils/types';
import { useParking } from 'src/providers/ParkingProvider';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { Form, FormControl, FormControlStack, FormItem, FormLabel, FormMessage, FormField } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';

type TierFormProps = {
    form: UseFormReturn<PricingPlanTierFormSchemaValues>;
    onSubmit: (values: PricingPlanTierFormSchemaValues) => void;
};

function TierForm({ onSubmit, form }: TierFormProps) {
    const { formatMessage } = useIntl();

    const [displayRate, setDisplayRate] = React.useState('');
    const { planId = '' } = useParams();
    const { parkingId } = useParking();
    const { data: pricingPlan } = useGetPricingPlan({ planId, parkingId });
    const { default_currency } = React.useContext(SettingsContext);
    const { tick_type, tick_minutes } = form.watch();

    const currency = React.useMemo(
        () => (pricingPlan?.currency ? formatMessage(CURRENCY_MAP[pricingPlan.currency]) : default_currency),
        [default_currency, formatMessage, pricingPlan],
    );

    // The tick is entered in the chosen unit and stored in minutes, as the backend keeps it
    const calculationTypeValue = React.useMemo(() => {
        switch (tick_type) {
            case TICK_TYPE.MINUTE:
                return 1;
            case TICK_TYPE.HOUR:
                return 60;
            case TICK_TYPE.DAY:
                return 60 * 24;
            case TICK_TYPE.WEEK:
                return 60 * 24 * 7;
            case TICK_TYPE.MONTH:
                return 60 * 24 * 30;
            default:
                return 1;
        }
    }, [tick_type]);

    // Switching the unit may leave a duration that is not a whole number of it - clear it instead
    React.useEffect(() => {
        if (!tick_minutes || Number.isInteger(tick_minutes / calculationTypeValue)) return;
        form.setValue('tick_minutes', 0);
    }, [form, calculationTypeValue, tick_minutes]);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <FormField
                control={form.control}
                name="start_minute"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(pricingPlanMessages.startMinute)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="number"
                                    min={0}
                                    placeholder={`${formatMessage(pricingPlanMessages.startMinute)}...`}
                                    value={field.value ?? ''}
                                    onChange={(e) => {
                                        const inputValue = e.target.value;
                                        if (!/^(?:\+?[0-9]+)?$/.test(inputValue)) return;
                                        field.onChange(inputValue === '' ? null : Number(inputValue));
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="end_minute"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(pricingPlanMessages.endMinute)}</FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="number"
                                    min={0}
                                    placeholder={`${formatMessage(pricingPlanMessages.endMinute)}...`}
                                    value={field.value ?? ''}
                                    onChange={(e) => {
                                        const inputValue = e.target.value;
                                        if (!/^(?:\+?[0-9]+)?$/.test(inputValue)) return;
                                        field.onChange(inputValue === '' ? null : Number(inputValue));
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="rate_with_tax"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...pricingPlanMessages.currencyRate} values={{ currency }} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder={`${formatMessage(pricingPlanMessages.currencyRate, { currency })}...`}
                                    value={
                                        displayRate !== ''
                                            ? displayRate
                                            : field.value != null
                                              ? (field.value / 100).toFixed(2)
                                              : ''
                                    }
                                    onChange={(e) => {
                                        const inputValue = e.target.value;

                                        if (!/^\d*\.?\d{0,2}$/.test(inputValue)) return;

                                        setDisplayRate(inputValue);

                                        const numeric = parseFloat(inputValue);
                                        if (!isNaN(numeric)) {
                                            field.onChange(Math.round(numeric * 100));
                                        } else {
                                            field.onChange(undefined);
                                        }
                                    }}
                                    onBlur={() => {
                                        if (displayRate === '' || isNaN(parseFloat(displayRate))) return;
                                        const formatted = parseFloat(displayRate).toFixed(2);
                                        setDisplayRate(formatted);
                                        field.onChange(Math.round(parseFloat(formatted) * 100));
                                    }}
                                    onKeyDown={(e) => {
                                        if (['-', '+', 'e'].includes(e.key)) e.preventDefault();
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <SelectFormField<PricingPlanTierFormSchemaValues>
                form={form}
                name="tick_type"
                label={formatMessage(pricingPlanMessages.tickTypeColumn)}
                options={Object.values(TICK_TYPE)}
                optionTranslationMap={TICK_TYPE_MAP}
            />

            <FormField
                control={form.control}
                name="tick_minutes"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(pricingPlanMessages.tickDuration)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="number"
                                    min={0}
                                    placeholder={`${formatMessage(pricingPlanMessages.tickDuration)}...`}
                                    value={field.value ? field.value / calculationTypeValue : ''}
                                    onChange={(e) => {
                                        const inputValue = e.target.value;
                                        if (!/^(?:\+?[0-9]+)?$/.test(inputValue)) return;
                                        field.onChange(
                                            inputValue === '' ? null : Number(inputValue) * calculationTypeValue,
                                        );
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
        </Form>
    );
}

export default TierForm;
